import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import Modalheader from 'components/modal/components/header';
import StyledInput from 'components/styledInput/StyledInput';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';

/**
 * The company note's editor, opened from any copy of the note.
 *
 * Not a modal of the shared system: that one shows a single modal at a time,
 * and the contract preview, itself a modal, has to stay open underneath. So
 * this dialog carries its own overlay, stacked above the shared modal (z-40 /
 * z-50) and below the expired-session dialog (z-[59] / z-[60]), which must
 * cover everything. It never touches the shared modal's context.
 *
 * `onSaved` receives the content as the api stored it (trimmed).
 */
const CompanyPostItDialog = ({ companyId, onClose, onSaved }) => {
  const { language } = UseContext() ?? {};
  const text = (key, fallback) =>
    language?.getValue?.(`postIt.${key}`, fallback) ?? fallback;

  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [content, setContent] = useState('');
  const dialogRef = useRef(null);

  // Focus goes back where it came from — the card, the pencil or the add
  // button — unless the save removed it from the page.
  useEffect(() => {
    const opener = document.activeElement;
    return () => {
      if (opener?.isConnected) opener.focus?.();
    };
  }, []);

  // The box takes focus while the note loads, the field once it is there.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const field =
      !isLoading && !loadFailed ? dialog.querySelector('textarea') : null;
    (field ?? dialog).focus();
  }, [isLoading, loadFailed]);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setLoadFailed(false);
    ApiDatabase.getPostItCompany(
      { idCompany: companyId },
      (data) => {
        if (!active) return;
        setContent(data?.postIt ?? '');
        setIsLoading(false);
      },
      (err) => {
        if (!active || err?.name === 'CanceledError') return;
        // An empty field after a failed read would save as a removal: the
        // field stays hidden rather than offer to wipe a note nobody saw.
        setLoadFailed(true);
        setIsLoading(false);
      },
    );
    return () => {
      active = false;
    };
  }, [companyId]);

  const requestClose = () => {
    if (!isSaving) onClose();
  };

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape' && !isSaving) onClose();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isSaving, onClose]);

  const handleSubmit = () => {
    setIsSaving(true);
    ApiDatabase.postPostItCompany(
      { idCompany: companyId, content },
      (data) => {
        setIsSaving(false);
        ApiDatabase.invalidateCompanyMissionsCache(companyId);
        toast.success(text('saved', 'Le post-it a bien été enregistré'));
        onSaved?.(data?.postIt ?? content.trim());
        onClose();
      },
      (err) => {
        if (err?.name === 'CanceledError') return;
        setIsSaving(false);
        // The server rejects an over-long note with { error, errorCode };
        // anything else is not worth spelling out to the user.
        const message = err?.response?.data?.error;
        toast.error(message || text('saveFailed', 'L’enregistrement a échoué'));
      },
    );
  };

  const canSubmit = !isLoading && !loadFailed;

  return ReactDOM.createPortal(
    <>
      <div className="flex fixed justify-center items-start inset-0 z-[56] overflow-y-auto outline-none focus:outline-none px-4">
        <div className="relative w-full max-w-3xl mx-auto flex justify-center my-6">
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            aria-label={text('title', 'Post-it')}
            className="relative flex flex-col w-full max-w-3xl max-h-[calc(100vh-3rem)] min-h-0 overflow-y-auto rounded-lg border-0 sh-lg bg-white outline-none focus:outline-none"
          >
            <Modalheader hide={requestClose} hideShow={true}>
              {text('title', 'Post-it')}
            </Modalheader>
            <ModalBody>
              <div className="flex flex-col gap-2.5 text-sm">
                {isLoading ? (
                  <div className="animate-pulse" data-testid="post-it-loading">
                    <div className="h-32 bg-gray-200 rounded w-full"></div>
                  </div>
                ) : loadFailed ? (
                  <div role="alert" className="text-red-500">
                    {text(
                      'loadFailed',
                      'Le post-it n’a pas pu être chargé. Fermez et réessayez.',
                    )}
                  </div>
                ) : (
                  <>
                    <div className="text-gray-500">
                      {text(
                        'where',
                        'Cette consigne s’affiche sur la fiche entreprise, le détail de relevé d’heures, le détail de mission, l’aperçu du contrat et le détail de facture.',
                      )}
                    </div>
                    <StyledInput
                      type="textarea"
                      label={text('label', 'Contenu')}
                      value={content}
                      placeholder={text(
                        'placeholder',
                        'Particularité de saisie, contact à prévenir…',
                      )}
                      onChange={(e) => setContent(e.target.value)}
                    />
                    {/* Clearing the field is how a note is removed: no
                        dedicated button for what the field already says. */}
                    <div className="text-gray-500">
                      {text(
                        'clearHint',
                        'Videz le champ pour retirer le post-it.',
                      )}
                    </div>
                  </>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              {isSaving ? (
                <>
                  <Button size="LG" color="SECONDARY">
                    {text('cancel', 'Annuler')}
                  </Button>
                  <Button size="LG" color="DISABLED">
                    {text('saving', 'Confirmation ...')}
                  </Button>
                </>
              ) : (
                <>
                  <Button size="LG" color="SECONDARY" onClick={requestClose}>
                    {text('cancel', 'Annuler')}
                  </Button>
                  <Button
                    size="LG"
                    color={canSubmit ? 'PRIMARY' : 'DISABLED'}
                    disabled={!canSubmit}
                    onClick={handleSubmit}
                  >
                    {text('confirm', 'Confirmer')}
                  </Button>
                </>
              )}
            </ModalFooter>
          </div>
        </div>
      </div>
      <div className="opacity-70 fixed inset-0 z-[55] bg-gray-700"></div>
    </>,
    document.getElementById('root-portal') || document.body,
  );
};

export default CompanyPostItDialog;
